import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { ep } from '../api/endpoints';

export interface ToukiFilters {
  search_word?: string;
  property_type?: '' | '土地' | '建物' | '区分建物';
  owner_is_corp?: boolean;
  has_lien?: boolean;
  watched_only?: boolean;
  check_status?: string;   // 最新の登記確認ステータスで絞り込み (pending=事件受付中 等)
  [key: string]: any;
}

export const DEFAULT_TOUKI_FILTERS: ToukiFilters = {
  search_word: '',
  property_type: '',
};

export interface ToukiSortConfig {
  field: string;
  order: 'asc' | 'desc';
}

export const GET_TOUKI_PROPERTIES_URL = ep('get_touki_properties');
export const GET_TOUKI_PROPERTY_DETAIL_URL = ep('get_touki_property_detail');
export const GET_TOUKI_DOCUMENT_PDF_URL = ep('get_touki_document_pdf');
export const SET_TOUKI_WATCH_TARGET_URL = ep('set_touki_watch_target');

/** 登記物件一覧のデータ取得フック (useCrmTable と同じ構造)。 */
export const useToukiTable = (
  initialFilters: ToukiFilters = DEFAULT_TOUKI_FILTERS,
  initialSort: ToukiSortConfig = { field: 'updated_at', order: 'desc' }
) => {
  const [data, setData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState<ToukiFilters>(initialFilters);
  const [sort, setSort] = useState<ToukiSortConfig>(initialSort);

  const lastRequestId = useRef(0);

  const fetchData = useCallback(async () => {
    const requestId = ++lastRequestId.current;
    setLoading(true);
    setData([]);
    try {
      const response = await axios.post(GET_TOUKI_PROPERTIES_URL, {
        page,
        pageSize,
        filters,
        sortField: sort.field,
        sortOrder: sort.order,
      });
      if (requestId === lastRequestId.current && response.data.status === 'success') {
        setData(response.data.rowsResult || []);
        setTotalCount(response.data.total || 0);
      }
    } catch (error) {
      if (requestId === lastRequestId.current) {
        console.error('Error fetching touki data:', error);
      }
    } finally {
      if (requestId === lastRequestId.current) {
        setLoading(false);
      }
    }
  }, [page, pageSize, filters, sort]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateFilters = useCallback((newFilters: ToukiFilters) => {
    setLoading(true);
    setData([]);
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPage(0);
  }, []);

  const handleSort = useCallback((field: string) => {
    setLoading(true);
    setData([]);
    setSort(prev => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc',
    }));
    setPage(0);
  }, []);

  // 対象マークのトグル後、全件再取得せず該当行の is_watched だけローカル更新する。
  const setRowWatched = useCallback((id: number, watched: boolean) => {
    setData(prev => prev.map(r => (r.id === id ? { ...r, is_watched: watched } : r)));
  }, []);

  // 「対象のみ」表示中に対象から外した行をリストから除去する (件数も連動)。
  const removeRow = useCallback((id: number) => {
    setData(prev => prev.filter(r => r.id !== id));
    setTotalCount(prev => Math.max(0, prev - 1));
  }, []);

  // Undo 用。除去した行を元の位置へ復元する (件数も連動)。
  const insertRow = useCallback((row: any, index: number) => {
    setData(prev => {
      const next = prev.slice();
      const i = Math.min(Math.max(index, 0), next.length);
      next.splice(i, 0, row);
      return next;
    });
    setTotalCount(prev => prev + 1);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setLoading(true);
    setData([]);
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setLoading(true);
    setData([]);
    setPageSize(newPageSize);
    setPage(0);
  }, []);

  return {
    data,
    totalCount,
    loading,
    page,
    pageSize,
    filters,
    sort,
    setPage: handlePageChange,
    setPageSize: handlePageSizeChange,
    updateFilters,
    handleSort,
    setRowWatched,
    removeRow,
    insertRow,
    refresh: fetchData,
  };
};
